import type {
  MiceOrderEventKind,
  MiceOrderEventView,
  MiceProfileField,
} from '@voll-intelligence/domain';
import { currentLocale, t } from '@web/i18n';
import { Button, Dot, type DotVariantProps } from '@web/ui/core';
import { useMiceActivity } from '../hooks/use_mice_activity';
import { formatFullDate } from './need_format';
import { genderLabel } from './profile_fields';

/** Cor do ponto pela fase em que o evento deixa o pedido. */
const TONE: Record<MiceOrderEventKind, NonNullable<DotVariantProps['tone']>> = {
  queued: 'ai',
  purchasing: 'ai',
  reserved: 'green',
  issued: 'green',
  unqueued: 'gray',
  released: 'gray',
  failed: 'red',
  indeterminate: 'red',
  price_drift: 'red',
  profile_written: 'green',
  profile_kept: 'gray',
  profile_write_failed: 'red',
  need_marked: 'gray',
  need_choice_changed: 'gray',
  need_asked_agent: 'ai',
  need_ordered_bulk: 'ai',
  email_corrected: 'gray',
  cost_center_changed: 'gray',
};

const FIELD = {
  name: 'mice.activity.profile.field.name',
  birthdate: 'mice.activity.profile.field.birthdate',
  cpf: 'mice.activity.profile.field.cpf',
  gender: 'mice.activity.profile.field.gender',
} as const;
const SOURCE = {
  sheet: 'mice.activity.profile.source.sheet',
  typed: 'mice.activity.profile.source.typed',
} as const;

const DIRECTION = { OUTWARD: 'mice.activity.outbound', RETURN: 'mice.activity.inbound' } as const;

/** Data do evento no fuso de quem olha, o mesmo da hora (`timeOf`). */
const dateOf = (iso: string): string =>
  new Intl.DateTimeFormat(currentLocale(), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(iso));

const timeOf = (iso: string): string =>
  new Intl.DateTimeFormat(currentLocale(), {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).format(new Date(iso));

/** Valor do cadastro como a tela mostra: nascimento em DD/MM/AAAA, sexo por extenso; o CPF já vem
 *  mascarado. */
const profileValueOf = (field: MiceProfileField | null, value: string | null): string => {
  if (value === null) return t('mice.activity.profile.empty');
  if (field === 'gender') return genderLabel(value);
  return field === 'birthdate' ? formatFullDate(value) : value;
};

/** "Op gravou o nascimento de Ana (vazio → 12/03/1985) · da planilha" (spec B6). */
function profileLineOf(e: MiceOrderEventView): string {
  const by = e.byName ?? e.by ?? '';
  const traveler = e.travelerName ?? e.travelerEmail;
  if (e.kind === 'profile_write_failed') {
    // O campo tentado vem desde a onda final do PR B; evento antigo, sem ele, fala do cadastro.
    return e.field
      ? t('mice.activity.profile.failedField', { by, traveler, field: t(FIELD[e.field]) })
      : t('mice.activity.profile.failed', { by, traveler });
  }
  const vars = {
    by,
    traveler,
    field: e.field ? t(FIELD[e.field]) : '',
    before: profileValueOf(e.field, e.before),
    after: profileValueOf(e.field, e.after),
  };
  if (e.kind === 'profile_kept') return t('mice.activity.profile.kept', vars);
  return t('mice.activity.profile.written', {
    ...vars,
    source: e.source ? t(SOURCE[e.source]) : '',
  });
}

function lineOf(e: MiceOrderEventView): string {
  if (
    e.kind === 'profile_written' ||
    e.kind === 'profile_kept' ||
    e.kind === 'profile_write_failed'
  ) {
    return profileLineOf(e);
  }
  return [
    e.travelerName ?? e.travelerEmail,
    e.direction ? t(DIRECTION[e.direction]) : null,
    t(`mice.activity.kind.${e.kind}`),
  ]
    .filter(Boolean)
    .join(' · ');
}

/** Falha do cadastro sem mensagem é a de comunicação (a única sem texto do servidor). */
const detailOf = (e: MiceOrderEventView): string | null =>
  e.detail ??
  (e.kind === 'profile_write_failed' ? t('mice.activity.profile.failedNoDetail') : null);

/** Um evento do feed: hora (com a data, no histórico do viajante), cor da fase, texto e detalhe. */
export function ActivityItem({
  event: e,
  withDate = false,
}: Readonly<{ event: MiceOrderEventView; withDate?: boolean }>): React.JSX.Element {
  const detail = detailOf(e);
  return (
    <li className="flex items-start gap-2 text-meta">
      <span className="font-mono text-micro tabular-nums text-muted-foreground">
        {withDate ? `${dateOf(e.at)} ${timeOf(e.at)}` : timeOf(e.at)}
      </span>
      <Dot tone={TONE[e.kind]} />
      <span className="flex min-w-0 flex-col">
        <span className="text-foreground">{lineOf(e)}</span>
        {detail ? <span className="text-micro text-muted-foreground">{detail}</span> : null}
      </span>
    </li>
  );
}

/** O que a compra e as decisões do cadastro fizeram, com hora, do mais novo para o mais antigo. */
export function ActivityFeed({ projectId }: Readonly<{ projectId: string }>): React.JSX.Element {
  const { events, isError, hasMore, loadMore } = useMiceActivity(projectId, true);
  return (
    <section aria-label={t('mice.activity.title')} className="flex min-w-0 flex-col gap-2">
      <span className="text-meta font-medium text-foreground">{t('mice.activity.title')}</span>
      {isError ? <p className="text-meta text-destructive">{t('mice.activity.error')}</p> : null}
      {!isError && events.length === 0 ? (
        <p className="text-meta text-muted-foreground">{t('mice.activity.empty')}</p>
      ) : null}
      <ol className="flex flex-col gap-1.5">
        {events.map((e) => (
          <ActivityItem key={e.id} event={e} />
        ))}
      </ol>
      {hasMore ? (
        <div>
          <Button size="sm" variant="ghost" onClick={loadMore}>
            {t('mice.activity.more')}
          </Button>
        </div>
      ) : null}
    </section>
  );
}
